/**
 * `modules/form.js`
 * - form関連の処理
 *
 * 使用方法:
 * - import {initForm} from "../modules/form.js";
 */


import {config} from "../config.js";
import {getContainer} from "../utils/index.js";
import {barbaEventObserver} from "../core/barba/utils/barbaEventObserver.js";


let flatpickrInstances = []; // Flatpickrのインスタンス管理用


export async function initForm(data) {
  console.log("📨 Initializing form modules...");

  // Flatpickr
  if (config.useFlatpickr) {
    const {default: flatpickr} = await import(/* webpackChunkName: "flatpickr" */ "flatpickr");

    resetForm();  // 既存のインスタンスを削除（リセット）

    setCalendar(flatpickr, '.datepicker', data);

    // plugin load
    // 不要な場合はコメントアウト
    setCalendarYearly(flatpickr, ".yearpicker", data); // 年月ピッカー
  }

  rangePicker(data); // 範囲選択ピッカー
}


/**
 * setCalendar from flatpickr
 * - 日付選択用のモジュール（datepickerだと使いづらいため）
 * */
function setCalendar(flatpickr, selector = '.datepicker', data = null) {
  const elm = getContainer(data).querySelector(selector);
  if (!elm) return;

  // flatpickrの初期化
  const instance = flatpickr(elm, {
    dateFormat: "Y年m月d日",
    altInput: true,
    altFormat: "Y年m月d日",
    locale: {
      firstDayOfWeek: 0, // 日曜日を週の始まりに設定
      weekdays: {
        shorthand: ["日", "月", "火", "水", "木", "金", "土"],
        longhand: ["日曜日", "月曜日", "火曜日", "水曜日", "木曜日", "金曜日", "土曜日"],
      },
      months: {
        shorthand: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"],
        longhand: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"],
      },
    },
  });

  flatpickrInstances.push(instance); // インスタンスを管理
}


/**
 * setCalendar from flatpickr
 * - 日付選択用のモジュール（datepickerだと使いづらいため）
 * */
async function setCalendarYearly(flatpickr, selector = ".yearpicker", data = null) {
  const elm = getContainer(data).querySelector(selector);
  if (!elm) return;

  // `monthSelectPlugin` を動的にインポート
  const { default: MonthSelectPlugin } = await import(/* webpackChunkName: "flatpickr-monthSelect" */ "flatpickr/dist/plugins/monthSelect/index.js");

  // `CSS` を動的に追加（すでに追加されていなければ）
  if (!document.getElementById("flatpickr-monthSelect-css")) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdn.jsdelivr.net/npm/flatpickr/dist/plugins/monthSelect/style.css";
    link.id = "flatpickr-monthSelect-css";
    document.head.appendChild(link);
  }

  // flatpickrの初期化（年月選択）
  const instance = flatpickr(elm, {
    dateFormat: "Y年m月",
    altInput: true,
    altFormat: "Y年m月",
    enableTime: false,
    defaultDate: "today",
    plugins: [new MonthSelectPlugin({ shorthand: true, dateFormat: "Y年m月", altFormat: "Y年m月" })],
    locale: {
      firstDayOfWeek: 0,
      months: {
        shorthand: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"],
        longhand: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"],
      },
    },
  });

  flatpickrInstances.push(instance); // インスタンスを管理
}


/**
 * カレンダーのインスタンスを削除する
 */
export function resetForm() {
  console.log("♻️ Resetting form modules...");

  // Flatpickr のインスタンスをすべて破棄
  flatpickrInstances.forEach((instance) => instance.destroy());
  flatpickrInstances = []; // 配列をクリア
}


/**
 * 範囲選択のピッカー
 */
function rangePicker(data, selector = "input[type='range']") {
  const container = getContainer(data);
  if (!container) return;

  const inputRanges = container.querySelectorAll(selector);
  const activeColor = "#1c2226";
  const inactiveColor = "#ECF0F4";
  const listeners = [];

  inputRanges.forEach(inputRange => {
    const nextSibling = inputRange.nextElementSibling;
    console.log(nextSibling);
    if (!nextSibling) return;

    if (nextSibling.classList.contains(".form_range_val")) {
      nextSibling.textContent = inputRange.value;
    }

    const rangePick = function () {
      const min = Number(this.min) || 0;
      const max = Number(this.max) || 100;
      const value = Number(this.value);

      const ratio = ((value - min) / (max - min)) * 100;
      this.style.background = `linear-gradient(90deg, ${activeColor} ${ratio}%, ${inactiveColor} ${ratio}%)`;
      nextSibling.textContent = this.value;
    };
    inputRange.addEventListener("input", rangePick);
    listeners.push([inputRange, "input", rangePick]);
  });

  barbaEventObserver.observeEvents(listeners);
}
